import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, CalendarCheck02, Copy01, CreditCard01, LineChartUp02,
  UploadCloud02, UserPlus01, Users01, UsersCheck
} from '@untitledui/icons';
import { toast } from 'react-toastify';
import { Button } from './untitled-ui/base/buttons/button';
import { memberService, MemberStatistics } from '../services/memberService';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<MemberStatistics | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const response = await memberService.getMemberStatistics();
        if (response.success && response.data) setStatistics(response.data);
        else toast.error('Failed to fetch statistics');
      } catch (error: any) {
        console.error('Error fetching statistics:', error);
        toast.error('Failed to fetch statistics: ' + error.message);
      }
    };
    fetchStatistics();
  }, []);

  const stats = [
    { label: 'Total members', value: statistics?.totalMembers || 0, icon: Users01, note: 'All registered profiles' },
    { label: 'Active members', value: statistics?.activeMembers || 0, icon: UsersCheck, note: 'Currently active plans' },
    { label: 'New members', value: statistics?.newMembers || 0, icon: UserPlus01, note: 'Recently registered' }
  ];

  const actions = [
    { to: '/register', label: 'Register member', description: 'Create a member profile', icon: UserPlus01 },
    { to: '/members', label: 'Member directory', description: 'Search and manage members', icon: Users01 },
    { to: '/attendance/dashboard', label: 'Attendance', description: 'Review member visits', icon: CalendarCheck02 },
    { to: '/payments', label: 'Record payment', description: 'Add a new collection', icon: CreditCard01 },
    { to: '/payments/dashboard', label: 'Payment reports', description: 'Track revenue and history', icon: LineChartUp02 },
    { to: '/payments/analytics', label: 'Payment analytics', description: 'Renewal signals and revenue outlook', icon: LineChartUp02 },
    { to: '/copy-workouts', label: 'Copy workouts', description: 'Reuse member plans', icon: Copy01 }
  ];

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div>
          <span className="dashboard-kicker">Operations overview</span>
          <h2>Good to see you again.</h2>
          <p>Here is what is happening at Cenadz Fitness today.</p>
        </div>
        <Button size="md" color="secondary" iconLeading={UploadCloud02} onPress={() => navigate('/attendance/upload')}>
          Import attendance
        </Button>
      </section>

      <section className="dashboard-stat-grid" aria-label="Member statistics">
        {stats.map(({ label, value, icon: Icon, note }) => (
          <article className="dashboard-stat-card" key={label}>
            <div className="dashboard-stat-heading">
              <span className="dashboard-stat-icon"><Icon /></span>
              <span className="dashboard-stat-menu">•••</span>
            </div>
            <strong>{value.toLocaleString()}</strong>
            <h3>{label}</h3>
            <p>{note}</p>
          </article>
        ))}
      </section>

      <section className="dashboard-panel">
        <div className="dashboard-panel-heading">
          <div><h2>Quick actions</h2><p>Common tasks for running the gym.</p></div>
        </div>
        <div className="dashboard-action-grid">
          {actions.map(({ to, label, description, icon: Icon }) => (
            <Link to={to} className="dashboard-action-card" key={to}>
              <span className="dashboard-action-icon"><Icon /></span>
              <span><strong>{label}</strong><small>{description}</small></span>
              <ArrowRight className="dashboard-action-arrow" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
